// Shared token refresh utility used by withAuth and Axios interceptors
import { concatAndEncodeBase64 } from "./index";

const XSM_PRODUCT_URL = process.env.REACT_APP_XSM_PRODUCT_URL as string;
const WS02_KEY = process.env.REACT_APP_WS02_KEY as string;
const WS02_SECRET = process.env.REACT_APP_WS02_SECRET as string;
const XSM_BASE_URL = process.env.REACT_APP_XSM_BASE_URL as string;
const USER_DOMAIN = process.env.REACT_APP_USER_DOMAIN as string;

const REFRESH_TOKEN_API_URL = `${XSM_BASE_URL}/xsm-api/v1/proxy/oauth2/token`;

// Refresh 5 minutes before the access token expires
export const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Calls the refresh-token API and updates localStorage tokens.
 * Returns true when refresh succeeds, false otherwise.
 */
export async function refreshAccessToken(): Promise<boolean> {
  // Reuse the in-flight request so concurrent callers don't trigger duplicate refreshes
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const currentRefreshToken = localStorage.getItem("refresh_token");
  if (!currentRefreshToken) {
    return false;
  }

  refreshInFlight = (async () => {
    try {
      const basicAuthorizationSecret = concatAndEncodeBase64(
        WS02_KEY,
        WS02_SECRET
      );

      const response = await fetch(REFRESH_TOKEN_API_URL, {
        method: "POST",
        headers: {
          Authorization: `Basic ${basicAuthorizationSecret}`,
          "Content-Type": "application/x-www-form-urlencoded",
          "Xsm-application": "xsi",
          "Xsm-Domain":
            USER_DOMAIN || (localStorage.getItem("domain") as string),
          "Xsm-iscompressed": "false",
          "X-Original-Origin": XSM_PRODUCT_URL,
        },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: currentRefreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to refresh access token");
      }

      const data = await response.json();
      const expiresInSeconds = data.expires_in;
      const expirationTime = Date.now() + expiresInSeconds * 1000;

      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      localStorage.setItem("expires_in", expirationTime.toString());
      if (data.id_token) {
        localStorage.setItem("id_token", data.id_token);
      }

      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}
